import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY, COLORS } from "@/lib/shop";

/**
 * Product gallery — ecommerce storefront style.
 * Main image + thumbnail row. Tap thumbnail or swipe to change image.
 * Color filter chips filter thumbnails by color.
 */
export default function ProductGallery() {
    const [active, setActive] = useState(0);
    const [colorFilter, setColorFilter] = useState("all");
    const current = GALLERY[active];
    const touchX = useRef(null);

    const filtered = colorFilter === "all"
        ? GALLERY
        : GALLERY.filter((g) => g.color === colorFilter);

    // Keep active index valid when filter changes
    const safeActive = Math.min(active, filtered.length - 1);
    const safeCurrent = filtered[safeActive];

    const prev = () => setActive((safeActive - 1 + filtered.length) % filtered.length);
    const next = () => setActive((safeActive + 1) % filtered.length);

    const onTouchStart = (e) => {
        touchX.current = e.touches[0].clientX;
    };
    const onTouchEnd = (e) => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 40) {
            if (dx < 0) next();
            else prev();
        }
        touchX.current = null;
    };

    return (
        <div className="relative" data-testid="product-gallery">
            {/* ─── Color Filter Chips ─── */}
            
            {/* ─── Main Image (swipeable) ─── */}
            <motion.div
                key={safeCurrent.id}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="relative rounded-2xl overflow-hidden shadow-lg shadow-[#1A1715]/15 border border-[#E4DDD3] touch-pan-y"
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                data-testid="gallery-main-image"
            >
                <img
                    src={safeCurrent.src}
                    alt={`${safeCurrent.label} — খাদি পাঞ্জাবি`}
                                    className="w-full aspect-[4/5] object-cover"
                    fetchpriority="high"
                />
                {/* Color badge */}
                <span className="absolute top-3 left-3 bg-[#1A1715]/80 text-[#F4EFE6] text-[11px] font-semibold px-2.5 py-0.5 rounded-full backdrop-blur-sm">
                    {safeCurrent.label}
                </span>
                {/* Arrow buttons (desktop) */}
                <button
                    type="button"
                    onClick={prev}
                    aria-label="আগের ছবি"
                                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 text-[#1E1B18] rounded-full p-1 shadow-md hover:bg-[#A84320] hover:text-[#FBF9F5] transition-colors duration-200"
                >
                                    <ChevronLeft size={15} />
                </button>
                <button
                    type="button"
                    onClick={next}
                    aria-label="পরের ছবি"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 text-[#1E1B18] rounded-full p-1 shadow-md hover:bg-[#A84320] hover:text-[#FBF9F5] transition-colors duration-200"
                >
                                    <ChevronRight size={15} />
                </button>
                {/* Counter */}
                <span className="absolute bottom-3 right-3 bg-[#1A1715]/80 text-[#F4EFE6] text-[11px] font-semibold px-2.5 py-0.5 rounded-full backdrop-blur-sm">
                    {safeActive + 1}/{filtered.length}
                </span>
            </motion.div>

            {/* ─── Thumbnail Row (compact, no gap) ─── */}
            <div className="mt-1 flex gap-1.5 overflow-x-auto pb-0.5" data-testid="gallery-thumbnails">
                {filtered.map((g, i) => (
                    <button
                        key={g.id}
                        type="button"
                        onClick={() => setActive(i)}
                        aria-label={g.label}
                        aria-pressed={i === safeActive}
                        data-testid={`gallery-thumb-${g.id}`}
                        className={`relative shrink-0 w-11 h-14 sm:w-16 sm:h-20 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                            i === safeActive ? "border-[#A84320] shadow-md shadow-[#A84320]/20" : "border-[#E4DDD3] hover:border-[#C9B896]"
                        }`}
                    >
                        <img src={g.src} alt={g.label} loading="lazy" className="w-full h-full object-cover" />
                        {i === safeActive && (
                            <span className="absolute inset-0 bg-[#A84320]/15" />
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
}